import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { ResponseScore } from "../../psyEvaluations.types";
import { FormatedField } from "../../../../types/fields.types";
import { RowDimension, ScoresCellContent, ScoresColumn, ScoresColumnContext } from "./scoresTable.types";
import { buildRowDimensions } from "./scoresTableUtils";
import { calculatedScoreColumn, dimensionNameColumn, isEmphasizedRow, rawScoreColumn } from "./scoresTableColumns";

type ScoresTablePdfProps = {
	scores: Array<ResponseScore>;
	formatedFields: Array<FormatedField>;
	levelColors: Record<string, string>;
	trailingColumns: Array<ScoresColumn>;
};

const DIMENSION_WIDTH = 187;
const NARROW_WIDTH = 56;
const MEAN_WIDTH = 96;

export const ScoresTablePdf = ({ scores, formatedFields, levelColors, trailingColumns }: ScoresTablePdfProps) => {
	const rowsDimensions = buildRowDimensions(scores);
	const context: ScoresColumnContext = { rowsDimensions, formatedFields, levelColors };
	const columns = [dimensionNameColumn, rawScoreColumn, calculatedScoreColumn, ...trailingColumns].filter((column) => column.isVisible(context));

	const textColumns = columns.filter((column) => getColumnContentType(column, rowsDimensions, context) === "text");
	const legendColumn = columns.find((column) => getColumnContentType(column, rowsDimensions, context) === "legend");
	const groups = buildLegendGroups(legendColumn, rowsDimensions, context);
	const hasMeanColumn = columns.some((column) => column.id === "mean");

	const textColumnWidth = (column: ScoresColumn) => {
		if (column.id === "dimension") return { width: DIMENSION_WIDTH };
		if (column.id === "mean") return { width: MEAN_WIDTH };
		if (column.getHeader(context).align === "center") return { width: NARROW_WIDTH };
		return { width: MEAN_WIDTH };
	};

	return (
		<View style={styles.table}>
			<View style={styles.row}>
				{textColumns.map((column) => {
					const header = column.getHeader(context);
					return (
						<View key={column.id} style={{ ...styles.cell, ...styles.headerCell, ...textColumnWidth(column) }}>
							<Text style={{ ...styles.headerText, ...(header.align === "center" ? styles.center : {}) }}>{header.label}</Text>
						</View>
					);
				})}
				{legendColumn && (
					<View style={{ ...styles.cell, ...styles.headerCell, ...styles.flex }}>
						<Text style={styles.headerText}>{legendColumn.getHeader(context).label}</Text>
					</View>
				)}
			</View>
			{groups.map((group) => {
				const groupRows = rowsDimensions.slice(group.startIndex, group.startIndex + group.rowSpan);
				const legendEmphasized = isEmphasizedRow(rowsDimensions[group.startIndex]);
				return (
					<View key={group.startIndex} style={styles.row}>
						<View>
							{groupRows.map((row, indexInGroup) => {
								const rowIndex = group.startIndex + indexInGroup;
								const emphasized = isEmphasizedRow(row);
								return (
									<View key={row.scores.id} style={styles.dataRow}>
										{textColumns.map((column) => {
											const cell = column.getCell(row, rowIndex, context);
											if (!cell || cell.content.type !== "text") return null;
											const isCenter = cell.align === "center";
											return (
												<View key={column.id} style={{ ...styles.cell, ...styles.middle, ...textColumnWidth(column), ...(cell.backgroundColor ? { backgroundColor: cell.backgroundColor } : {}) }}>
													<Text style={{ ...styles.cellText, ...(isCenter ? styles.center : {}), ...(emphasized ? styles.bold : {}) }}>{cell.content.value.replace(/\n/g, " ")}</Text>
												</View>
											);
										})}
									</View>
								);
							})}
						</View>
						{legendColumn && group.content && (
							<View style={{ ...styles.cell, ...styles.flex }}>
								<LegendEntriesPdf content={group.content} emphasized={legendEmphasized} hasMeanColumn={hasMeanColumn} />
							</View>
						)}
					</View>
				);
			})}
		</View>
	);
};

type LegendContent = Extract<ScoresCellContent, { type: "legend" }>;

type LegendGroup = {
	startIndex: number;
	rowSpan: number;
	content: LegendContent | null;
};

type LegendEntriesPdfProps = {
	content: LegendContent;
	emphasized: boolean;
	hasMeanColumn: boolean;
};

const LegendEntriesPdf = ({ content, emphasized, hasMeanColumn }: LegendEntriesPdfProps) => {
	return (
		<View style={{ width: "100%" }}>
			{content.entries.map((entry, index) => {
				const wrapThreshold = hasMeanColumn ? 36 : 47;
				const entryHeight = entry.description.length > wrapThreshold ? 20 : 10;
				return (
					<View key={entry.key} style={{ width: "94%", flexDirection: "row", height: entryHeight, alignItems: content.entryAlign === "center" ? "center" : "flex-start", marginTop: index === 0 ? 0 : 4 }}>
						<View style={{ ...styles.swatch, ...(entry.swatchColor ? { backgroundColor: entry.swatchColor } : {}) }} />
						<Text style={{ ...styles.legendText, ...(emphasized ? styles.bold : {}) }}>{entry.rangeText != null ? `${entry.rangeText}: ${entry.description}` : entry.description}</Text>
					</View>
				);
			})}
		</View>
	);
};

const getColumnContentType = (column: ScoresColumn, rowsDimensions: Array<RowDimension>, context: ScoresColumnContext) => {
	for (let rowIndex = 0; rowIndex < rowsDimensions.length; rowIndex++) {
		const cell = column.getCell(rowsDimensions[rowIndex], rowIndex, context);
		if (cell) return cell.content.type;
	}
	return "text";
};

const buildLegendGroups = (legendColumn: ScoresColumn | undefined, rowsDimensions: Array<RowDimension>, context: ScoresColumnContext) => {
	if (!legendColumn) return [{ startIndex: 0, rowSpan: rowsDimensions.length, content: null }] as Array<LegendGroup>;
	const groups: Array<LegendGroup> = [];
	rowsDimensions.forEach((row, rowIndex) => {
		const cell = legendColumn.getCell(row, rowIndex, context);
		if (cell && cell.content.type === "legend") {
			groups.push({ startIndex: rowIndex, rowSpan: cell.rowSpan ?? 1, content: cell.content });
		}
	});
	return groups;
};

const styles = StyleSheet.create({
	table: { borderTopWidth: 0.5, borderLeftWidth: 0.5, borderColor: "#999999", borderStyle: "solid", marginBottom: 12 },
	row: { flexDirection: "row" },
	dataRow: { flexDirection: "row", flexGrow: 1 },
	middle: { justifyContent: "center" },
	flex: { flexGrow: 1, flexShrink: 1, flexBasis: 0 },
	cell: { flexShrink: 0, borderRightWidth: 0.5, borderBottomWidth: 0.5, borderColor: "#999999", borderStyle: "solid", paddingVertical: 3, paddingHorizontal: 5 },
	headerCell: { justifyContent: "center" },
	center: { textAlign: "center" },
	headerText: { fontSize: 8, fontFamily: "Montserrat", fontWeight: "bold", color: "#2c3e50" },
	cellText: { fontSize: 8, fontFamily: "Montserrat" },
	bold: { fontWeight: "bold" },
	swatch: { width: 8, height: 8, marginRight: 5, marginTop: 1, borderWidth: 0.5, borderColor: "rgba(0,0,0,0.2)", borderStyle: "solid" },
	legendText: { fontSize: 8, fontFamily: "Montserrat", flexGrow: 1, flexShrink: 1, textAlign: "left" },
});
